import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckSquare, Calendar, User, Tag, FolderKanban, X } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const PRIORITY_OPTIONS = [
  { value: 'baixa', label: 'Baixa', color: 'text-slate-600', bg: 'bg-slate-100' },
  { value: 'media', label: 'Média', color: 'text-blue-700', bg: 'bg-blue-100' },
  { value: 'alta', label: 'Alta', color: 'text-orange-700', bg: 'bg-orange-100' },
  { value: 'urgente', label: 'Urgente', color: 'text-red-700', bg: 'bg-red-100' },
];

export default function GlobalTaskCreateModal({ isOpen, onClose, onSuccess, projects, users }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    assignee_id: 'none',
    priority: 'media',
    due_date: ''
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      project_id: '',
      title: '',
      assignee_id: 'none',
      priority: 'media',
      due_date: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error('Selecione um projeto primeiro.');
      return;
    }
    if (!formData.title.trim()) {
       toast.error('Digite o título da tarefa.');
       return;
    }

    setLoading(true);
    
    try {
      const project = projects.find(p => p.id === formData.project_id);
      if (!project) throw new Error('Projeto não encontrado');

      const newTask = {
        id: crypto.randomUUID(),
        title: formData.title,
        status: 'pendente',
        assignee_id: formData.assignee_id === 'none' ? user?.id : formData.assignee_id,
        priority: formData.priority,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        created_at: new Date().toISOString()
      };

      const updatedTasks = [...(project.tasks || []), newTask];
      const payload = { ...project, tasks: updatedTasks };

      // Sanitizar assignees como em Projetos
      payload.tasks = payload.tasks.map(t => ({
         ...t,
         assignee_id: t.assignee_id === 'none' ? null : t.assignee_id
      }));

      await api.put(`/projects/${project.id}`, payload);
      toast.success('Tarefa atribuída com sucesso!');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erro ao adicionar tarefa');
    } finally {
      setLoading(false);
    }
  };

  const currentPriority = PRIORITY_OPTIONS.find(p => p.value === formData.priority);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden border-0 shadow-2xl rounded-2xl gap-0 [&>button:last-child]:hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-6 pt-6 pb-8 text-white relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <CheckSquare className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white/70">Nova Tarefa Rápida</span>
          </div>
          <h2 className="text-xl font-bold text-white leading-tight pr-8">
            Atribuir Tarefa Operacional
          </h2>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="bg-white px-6 py-5 flex flex-col gap-4">

          {/* Projeto */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <FolderKanban className="h-3.5 w-3.5" /> Projeto Destino *
            </Label>
            <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
              <SelectTrigger className="h-10 border-slate-200">
                <SelectValue placeholder="Selecione a qual projeto esta tarefa pertence" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nome da Tarefa */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <CheckSquare className="h-3.5 w-3.5" /> O que precisa ser feito? *
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Ex: Atualizar banners do site..."
              className="border-slate-200 h-10"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Responsável */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Atribuir Para
              </Label>
              <Select value={formData.assignee_id} onValueChange={(v) => setFormData({ ...formData, assignee_id: v })}>
                <SelectTrigger className="h-10 border-slate-200">
                  <SelectValue placeholder="Membro da equipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum responsável</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prioridade */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> Prioridade
              </Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="h-10 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className={`font-semibold ${p.color}`}>{p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Prazo */}
          <div className="w-1/2 pr-1.5">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Prazo (Opcional)
              </Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-200 text-slate-600">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md"
            >
              {loading ? 'Adicionando...' : 'Adicionar Tarefa'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
